import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Alert,
  Autocomplete,
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  IconButton,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  Delete as DeleteIcon,
  Edit as EditIcon,
  ToggleOff as ToggleOffIcon,
  ToggleOn as ToggleOnIcon,
  Visibility as VisibilityIcon,
  AddCircle as AddCircleIcon,
  AttachFile as AttachFileIcon,
  Handshake as HandshakeIcon,
  PriceCheck as PriceCheckIcon,
  Inventory2 as Inventory2Icon,
  Business as BusinessIcon,
} from '@mui/icons-material';
import ProcurementContainer from '../../procurement/containers/ProcurementContainer';
import DropzoneArea from '../../common/DropzoneArea';
import {
  ProcurementContract,
  useCreateProcurementContractMutation,
  useDeleteProcurementContractByIdMutation,
  useGetProcurementContractByIdQuery,
  useGetProcurementContractsQuery,
  useUpdateProcurementContractByIdMutation,
} from '../../../redux/procurementContractsApi';
import { useGetStakeholdersByTypeByTypeQuery, Attachment, EntityIdentity } from '../../../redux/vmsApi';
import { getStakeholderStatusBadges } from '../../../utils/stakeholderStatusUtil';
import { formatCurrency } from '../../../utils/currencyUtil';

type ContractFormState = {
  title: string;
  supplierId: string;
  attachments: Attachment[];
};

const initialFormState: ContractFormState = {
  title: '',
  supplierId: '',
  attachments: [],
};

const APP_GRADIENT = 'linear-gradient(135deg, #1976d2 30%, #F7C914 90%)';

const ContractsListPage: React.FC = () => {
  const navigate = useNavigate();
  const { data: suppliers = [], isLoading: suppliersLoading } = useGetStakeholdersByTypeByTypeQuery({ type: 'Supplier' });
  const { data: contracts = [], isLoading: contractsLoading, isError: isContractsError } = useGetProcurementContractsQuery();
  const [createContract, { isLoading: isCreating }] = useCreateProcurementContractMutation();
  const [updateContract, { isLoading: isUpdating }] = useUpdateProcurementContractByIdMutation();
  const [deleteContract, { isLoading: isDeleting }] = useDeleteProcurementContractByIdMutation();

  const [formState, setFormState] = useState<ContractFormState>(initialFormState);
  const [editingContractId, setEditingContractId] = useState<string | null>(null);
  const [feedback, setFeedback] = useState<{ severity: 'success' | 'error'; message: string } | null>(null);
  const [deleteCandidate, setDeleteCandidate] = useState<ProcurementContract | null>(null);
  const [viewContractId, setViewContractId] = useState<string | null>(null);
  const [attachmentContractId, setAttachmentContractId] = useState<string | null>(null);
  const [dialogAttachments, setDialogAttachments] = useState<Attachment[]>([]);
  const [isSavingAttachments, setIsSavingAttachments] = useState(false);

  const { data: selectedContract, isLoading: selectedContractLoading } = useGetProcurementContractByIdQuery(
    { id: viewContractId ?? '' },
    { skip: !viewContractId },
  );

  const selectedSupplier = suppliers.find((supplier) => String(supplier.id) === formState.supplierId);
  const isSubmitting = isCreating || isUpdating;
  const isEditing = Boolean(editingContractId);
  const canSubmit = Boolean(formState.title.trim() && formState.supplierId) && !isSubmitting;

  const summary = useMemo(() => {
    const activeCount = contracts.filter((contract) => contract.active).length;
    const totalValue = contracts.reduce((sum, contract) => sum + Number(contract.totalValue ?? 0), 0);
    return {
      total: contracts.length,
      active: activeCount,
      totalValue,
    };
  }, [contracts]);

  useEffect(() => {
    if (!feedback) return;
    const timer = window.setTimeout(() => setFeedback(null), 5000);
    return () => window.clearTimeout(timer);
  }, [feedback]);

  const setField = <K extends keyof ContractFormState>(field: K, value: ContractFormState[K]) => {
    setFormState((current) => ({ ...current, [field]: value }));
  };

  const resetForm = () => {
    setFormState(initialFormState);
    setEditingContractId(null);
  };

  const mapContractToFormState = (contract: ProcurementContract): ContractFormState => ({
    title: contract.title ?? '',
    supplierId: String(contract.supplier?.id ?? ''),
    attachments: contract.attachments ?? [],
  });

  const buildPayload = () => {
    const supplier: EntityIdentity = {
      id: selectedSupplier?.id,
      name: selectedSupplier?.name,
    };

    return {
      title: formState.title.trim(),
      supplier,
      attachments: formState.attachments,
      active: false,
    };
  };

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!canSubmit) return;

    try {
      const payload = buildPayload();

      if (editingContractId) {
        await updateContract({
          id: editingContractId,
          procurementContractPartial: payload as never,
        }).unwrap();
        setFeedback({ severity: 'success', message: 'Contract updated successfully.' });
      } else {
        await createContract({
          newProcurementContract: payload as never,
        }).unwrap();
        setFeedback({ severity: 'success', message: 'Contract created successfully.' });
      }

      resetForm();
    } catch (error) {
      console.error('Failed to save procurement contract:', error);
      setFeedback({ severity: 'error', message: 'Failed to save contract. Please try again.' });
    }
  };

  const handleOpenAttachmentDialog = (contract: ProcurementContract) => {
    setAttachmentContractId(contract.id ?? null);
    setDialogAttachments(contract.attachments ?? []);
  };

  const handleSaveAttachments = async () => {
    if (!attachmentContractId) return;

    setIsSavingAttachments(true);
    try {
      await updateContract({
        id: attachmentContractId,
        procurementContractPartial: { attachments: dialogAttachments },
      }).unwrap();
      setFeedback({ severity: 'success', message: 'Supporting documents updated successfully.' });
      setAttachmentContractId(null);
    } catch (error) {
      console.error('Failed to update attachments:', error);
      setFeedback({ severity: 'error', message: 'Failed to update supporting documents. Please try again.' });
    } finally {
      setIsSavingAttachments(false);
    }
  };

  const handleEdit = (contract: ProcurementContract) => {
    if (contract.active || !contract.id) return;
    setEditingContractId(contract.id);
    setFormState(mapContractToFormState(contract));
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleToggleActive = async (contract: ProcurementContract) => {
    if (!contract.id) return;

    try {
      await updateContract({
        id: contract.id,
        procurementContractPartial: { active: !contract.active },
      }).unwrap();
      setFeedback({
        severity: 'success',
        message: `Contract ${contract.active ? 'deactivated' : 'activated'} successfully.`,
      });
      if (editingContractId === contract.id && contract.active) {
        resetForm();
      }
    } catch (error) {
      console.error('Failed to toggle contract status:', error);
      setFeedback({ severity: 'error', message: 'Failed to change contract status. Please try again.' });
    }
  };

  const handleDelete = async () => {
    if (!deleteCandidate?.id) return;

    try {
      await deleteContract({ id: deleteCandidate.id }).unwrap();
      setFeedback({ severity: 'success', message: 'Contract deleted successfully.' });
      if (editingContractId === deleteCandidate.id) {
        resetForm();
      }
      setDeleteCandidate(null);
    } catch (error) {
      console.error('Failed to delete contract:', error);
      setFeedback({ severity: 'error', message: 'Failed to delete contract. Please try again.' });
    }
  };

  return (
    <ProcurementContainer>
      <Stack spacing={4}>
        <Card
          sx={{
            background: APP_GRADIENT,
            color: 'white',
            padding: 3,
            borderRadius: 2,
            boxShadow: '0 4px 20px rgba(0, 0, 0, 0.1)',
            textAlign: 'center',
            position: 'relative',
            overflow: 'hidden',
            marginTop: 1,
            marginX: 2,
          }}
        >
          <CardContent sx={{ position: 'relative', zIndex: 2 }}>
            <Stack spacing={2} alignItems="center">
              <Avatar sx={{ bgcolor: 'rgba(255,255,255,0.2)', width: 56, height: 56 }}>
                <HandshakeIcon fontSize="large" />
              </Avatar>
              <Box>
                <Typography variant="h4" component="h2" sx={{ fontWeight: 'bold', textTransform: 'uppercase', letterSpacing: 1.5, color: 'white' }}>
                  Procurement Contracts
                </Typography>
                <Typography sx={{ opacity: 0.9, mt: 0.5 }}>
                  Manage supplier contracts, values and supporting documentation in one place.
                </Typography>
              </Box>

              <Grid container spacing={2} justifyContent="center" sx={{ maxWidth: 520 }}>
                <Grid item xs={4}>
                  <Stack alignItems="center" spacing={0.5} sx={{ bgcolor: 'rgba(255,255,255,0.15)', borderRadius: 3, py: 1.5 }}>
                    <Typography variant="h5" sx={{ fontWeight: 800 }}>{summary.total}</Typography>
                    <Typography variant="caption" sx={{ opacity: 0.85 }}>Contracts</Typography>
                  </Stack>
                </Grid>
                <Grid item xs={4}>
                  <Stack alignItems="center" spacing={0.5} sx={{ bgcolor: 'rgba(255,255,255,0.15)', borderRadius: 3, py: 1.5 }}>
                    <Typography variant="h5" sx={{ fontWeight: 800 }}>{summary.active}</Typography>
                    <Typography variant="caption" sx={{ opacity: 0.85 }}>Active</Typography>
                  </Stack>
                </Grid>
                <Grid item xs={4}>
                  <Stack alignItems="center" spacing={0.5} sx={{ bgcolor: 'rgba(255,255,255,0.15)', borderRadius: 3, py: 1.5 }}>
                    <Typography variant="h6" sx={{ fontWeight: 800 }}>
                      {formatCurrency(summary.totalValue, 'usd')}
                    </Typography>
                    <Typography variant="caption" sx={{ opacity: 0.85 }}>Total Value</Typography>
                  </Stack>
                </Grid>
              </Grid>
            </Stack>
          </CardContent>
        </Card>

        {feedback && <Alert severity={feedback.severity}>{feedback.message}</Alert>}

        <Card elevation={3} sx={{ borderRadius: 3, borderTop: '4px solid #1976d2' }}>
          <CardContent>
            <Stack component="form" spacing={3} onSubmit={handleSubmit}>
              <Box>
                <Typography variant="h6" sx={{ fontWeight: 700 }}>
                  {isEditing ? 'Update Contract' : 'Create New Contract'}
                </Typography>
                <Typography color="text.secondary">
                  Capture the contract title, supplier and supporting documents, then submit.
                </Typography>
              </Box>

              <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
                <TextField
                  fullWidth
                  label="Title"
                  value={formState.title}
                  onChange={(event) => setField('title', event.target.value)}
                />

                <Autocomplete
                  fullWidth
                  options={suppliers}
                  loading={suppliersLoading}
                  getOptionLabel={(option) => option.name ?? ''}
                  getOptionDisabled={(option) => !option.isStakeholderVerified}
                  isOptionEqualToValue={(option, value) => String(option.id) === String(value.id)}
                  value={suppliers.find((supplier) => String(supplier.id) === formState.supplierId) ?? null}
                  onChange={(_, value) => setField('supplierId', value?.id ? String(value.id) : '')}
                  renderOption={(props, option) => (
                    <li {...props} key={option.id}>
                      <Stack spacing={0.5} sx={{ width: '100%' }}>
                        <Typography variant="body2">
                          {option.name}
                          {!option.isStakeholderVerified && ' (Unverified)'}
                        </Typography>
                        <Stack direction="row" flexWrap="wrap" gap={0.5}>
                          {getStakeholderStatusBadges(option).map((badge) => (
                            <Chip
                              key={badge.label}
                              label={badge.label}
                              color={badge.color as any}
                              variant={badge.variant}
                              size="small"
                              sx={{ fontWeight: 'bold' }}
                            />
                          ))}
                        </Stack>
                      </Stack>
                    </li>
                  )}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Supplier"
                      placeholder="Select a supplier stakeholder"
                      InputProps={{
                        ...params.InputProps,
                        startAdornment: <BusinessIcon fontSize="small" sx={{ color: 'text.disabled', mr: 1 }} />,
                      }}
                    />
                  )}
                />
              </Stack>

              <Box>
                <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 600 }}>
                  Supporting Documents
                </Typography>
                <DropzoneArea
                  key={`contract-attachments-${editingContractId ?? 'create'}-${formState.attachments.length}`}
                  fieldName="attachments"
                  attachments={formState.attachments}
                  setAttachments={(attachments) => setField('attachments', attachments)}
                />
              </Box>

              <Stack direction="row" spacing={2} justifyContent="flex-end">
                {isEditing && (
                  <Button variant="outlined" onClick={resetForm} disabled={isSubmitting}>
                    Cancel
                  </Button>
                )}
                <Button variant="contained" type="submit" disabled={!canSubmit} sx={{ bgcolor: '#1976d2', '&:hover': { bgcolor: '#125ea3' } }}>
                  {isSubmitting ? 'Saving...' : isEditing ? 'Update Contract' : 'Create Contract'}
                </Button>
              </Stack>
            </Stack>
          </CardContent>
        </Card>

        <Paper elevation={2} sx={{ borderRadius: 3 }}>
          <Stack spacing={2} p={3}>
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 700 }}>
                Contract List
              </Typography>
              <Typography color="text.secondary">
                Active contracts can be deactivated, but cannot be edited or deleted until inactive again.
              </Typography>
            </Box>

            {isContractsError && <Alert severity="error">Failed to load procurement contracts.</Alert>}

            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Title</TableCell>
                    <TableCell>Supplier</TableCell>
                    <TableCell align="right">Total Value</TableCell>
                    <TableCell>Documents</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {contractsLoading ? (
                    <TableRow>
                      <TableCell colSpan={6}>
                        <Stack direction="row" spacing={2} alignItems="center" justifyContent="center" py={4}>
                          <CircularProgress size={24} />
                          <Typography>Loading contracts...</Typography>
                        </Stack>
                      </TableCell>
                    </TableRow>
                  ) : contracts.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={6}>
                        <Typography color="text.secondary" py={3} textAlign="center">
                          No procurement contracts found.
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    contracts.map((contract) => (
                      <TableRow key={contract.id ?? `${contract.supplier?.id}-${contract.title}`} hover>
                        <TableCell>{contract.title || 'Untitled Contract'}</TableCell>
                        <TableCell>
                          <Stack direction="row" spacing={1} alignItems="center">
                            <BusinessIcon fontSize="small" sx={{ color: '#1976d2' }} />
                            <span>{contract.supplier?.name || '-'}</span>
                          </Stack>
                        </TableCell>
                        <TableCell align="right">{formatCurrency(Number(contract.totalValue ?? 0), 'usd')}</TableCell>
                        <TableCell>{contract.attachments?.length ?? 0}</TableCell>
                        <TableCell>
                          <Chip
                            label={contract.active ? 'Active' : 'Inactive'}
                            color={contract.active ? 'success' : 'default'}
                            size="small"
                          />
                        </TableCell>
                        <TableCell align="right">
                          <Stack direction="row" spacing={1} justifyContent="flex-end">
                            <Tooltip title="Manage Items">
                              <IconButton onClick={() => navigate(`/procurement-contracts/management/${contract.id}/items`)}>
                                <AddCircleIcon fontSize="small" sx={{ color: '#1976d2' }} />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="View Contract">
                              <IconButton onClick={() => setViewContractId(contract.id ?? null)}>
                                <VisibilityIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Manage Attachments">
                              <IconButton onClick={() => handleOpenAttachmentDialog(contract)}>
                                <AttachFileIcon fontSize="small" color="action" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title={contract.active ? 'Deactivate Contract' : 'Activate Contract'}>
                              <IconButton onClick={() => handleToggleActive(contract)} disabled={isUpdating}>
                                {contract.active ? <ToggleOffIcon fontSize="small" /> : <ToggleOnIcon fontSize="small" color="success" />}
                              </IconButton>
                            </Tooltip>
                            {!contract.active && (
                              <Tooltip title="Edit Contract">
                                <IconButton onClick={() => handleEdit(contract)}>
                                  <EditIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            )}
                            {!contract.active && (
                              <Tooltip title="Delete Contract">
                                <IconButton onClick={() => setDeleteCandidate(contract)} disabled={isDeleting}>
                                  <DeleteIcon fontSize="small" color="error" />
                                </IconButton>
                              </Tooltip>
                            )}
                          </Stack>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Stack>
        </Paper>
      </Stack>

      <Dialog open={Boolean(viewContractId)} onClose={() => setViewContractId(null)} fullWidth maxWidth="md">
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Inventory2Icon sx={{ color: '#1976d2' }} /> Contract Details
        </DialogTitle>
        <DialogContent dividers>
          {selectedContractLoading ? (
            <Stack direction="row" spacing={2} alignItems="center" justifyContent="center" py={4}>
              <CircularProgress size={24} />
              <Typography>Loading contract details...</Typography>
            </Stack>
          ) : selectedContract ? (
            <Stack spacing={2}>
              <Box>
                <Typography variant="subtitle2" color="text.secondary">Title</Typography>
                <Typography>{selectedContract.title || 'Untitled Contract'}</Typography>
              </Box>
              <Divider />
              <Box>
                <Typography variant="subtitle2" color="text.secondary">Supplier</Typography>
                <Typography>{selectedContract.supplier?.name || '-'}</Typography>
              </Box>
              <Divider />
              <Box>
                <Typography variant="subtitle2" color="text.secondary">Total Value</Typography>
                <Typography sx={{ fontWeight: 700, color: '#1976d2' }}>
                  <PriceCheckIcon fontSize="small" sx={{ verticalAlign: 'middle', mr: 0.5 }} />
                  {formatCurrency(Number(selectedContract.totalValue ?? 0), 'usd')}
                </Typography>
              </Box>
              <Divider />
              <Box>
                <Typography variant="subtitle2" color="text.secondary">Status</Typography>
                <Chip
                  label={selectedContract.active ? 'Active' : 'Inactive'}
                  color={selectedContract.active ? 'success' : 'default'}
                  size="small"
                />
              </Box>
              <Divider />
              <Box>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>Supporting Documents</Typography>
                {selectedContract.attachments?.length ? (
                  <Stack spacing={1}>
                    {selectedContract.attachments.map((attachment, index) => (
                      <Button
                        key={`${attachment.url}-${index}`}
                        variant="text"
                        sx={{ justifyContent: 'flex-start', px: 0 }}
                        onClick={() => window.open(attachment.url, '_blank', 'noopener,noreferrer')}
                      >
                        {attachment.name}
                      </Button>
                    ))}
                  </Stack>
                ) : (
                  <Typography color="text.secondary">No supporting documents uploaded.</Typography>
                )}
              </Box>
            </Stack>
          ) : (
            <Typography color="text.secondary">Contract not found.</Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setViewContractId(null)}>Close</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={Boolean(attachmentContractId)} onClose={() => setAttachmentContractId(null)} fullWidth maxWidth="sm">
        <DialogTitle>Manage Supporting Documents</DialogTitle>
        <DialogContent>
          <DropzoneArea
            key={`attachment-dialog-${attachmentContractId ?? 'none'}-${dialogAttachments.length}`}
            fieldName="attachments"
            attachments={dialogAttachments}
            setAttachments={setDialogAttachments}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAttachmentContractId(null)} disabled={isSavingAttachments}>
            Cancel
          </Button>
          <Button variant="contained" onClick={handleSaveAttachments} disabled={isSavingAttachments}>
            {isSavingAttachments ? 'Saving...' : 'Save Documents'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={Boolean(deleteCandidate)} onClose={() => setDeleteCandidate(null)}>
        <DialogTitle>Delete Contract</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete "{deleteCandidate?.title}"? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteCandidate(null)}>Cancel</Button>
          <Button onClick={handleDelete} color="error" variant="contained" disabled={isDeleting}>
            {isDeleting ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </ProcurementContainer>
  );
};

export default ContractsListPage;
